import MainLayout from "../../../layouts/MainLayout";
import { T } from "../../../helpers/Language";
import { Button, Card, Col, Container, Form, Row } from "react-bootstrap";
import AppOverlay from "../../../components/Overlay/Overlay";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getReportOptions } from "../../../api/actions/FFDAReport";
import { getHashReport, getReportHashed } from "../../../api/actions/Reports";

const ReportPeriodToNumber = (period) => (period === 'MONTHLY' ? 1 : 0);

const DEFAULT_CAN_ACCESS = (permisionKey) => function (reportAccess, reportOptions) {
    const reportOption = reportOptions.reports[this.code] || {};
    if (reportAccess.chapel === '') {
        if (reportOption.canCombineOption !== true) {
            return false;
        }
    }
    if (reportAccess.chapel != '') {
        if (reportOption.hasChapelReport !== true) {
            return false;
        }
    }
    return reportAccess && reportAccess[permisionKey];
}

const REPORT_LIST = [{
    code: 'PROFIT_N_LOSS',
    label: 'Profit and Loss',
    canAccess: DEFAULT_CAN_ACCESS('profitAndLoss'),
},{
    code: 'BALANCE_SHEET',
    label: 'Balance Sheet',
    canAccess: DEFAULT_CAN_ACCESS('balanceSheet'),
},{
    code: 'SALES_ACTIVITY',
    label: 'Sales Activity',
    canAccess: (reportAccess, reportOptions) => {
        const reportAvailable = reportOptions.reportAvailable || {};
        return reportAccess?.salesActivity && reportAvailable.salesActivity === true;
    }
},{
    code: 'AGING',
    label: 'Aging',
    canAccess: DEFAULT_CAN_ACCESS('aging'),
}]

const PERIOD_LIST = {
    MONTHLY: 'Monthly',
    FINAL: 'Final'
};

const ChapelSelector = ({
    reportOptions,
    currentChapel,
    onChange
}) => {
    const chapels = useMemo(() => {
        if (reportOptions?.userChapels) {
            return reportOptions.userChapels;
        }
        return [];
    }, [reportOptions, reportOptions.userChapels]);
    const memberNumber = useMemo(() => reportOptions['memberNumber'] || '', [reportOptions]);
    const canCombine = useMemo(() => reportOptions['canCombine'] || '', [reportOptions]);
    const getChapelNameFrom = useCallback((chapel) => {
        let str = memberNumber;
        if (chapel.chapel === '') {
            if (canCombine) {
                str += ' - combined';
            }
        } else {
            str += ' - Chapel ' + chapel.chapel;
        }
        return str;
    }, [memberNumber]);
    return (
        <Form.Group className="mb-3">
            <Form.Label className="fw-bold">Chapel</Form.Label>
            <Form.Control
                as="select"
                value={currentChapel?.id || '__NONE__'}
                onChange={(e) => {
                    const { value } = e.target;
                    const chapel = chapels.find(chapel => chapel.id === value);
                    onChange(chapel);
                }}
            >
                <option value="__NONE__">Select a Chapel</option>
                {chapels.map((chapel, idx) => (
                    <option key={chapel.id} value={chapel.id}>{getChapelNameFrom(chapel)}</option>
                ))}
            </Form.Control>
        </Form.Group>
    );

}

const ReportSelector = ({
    reports, currentReport, onChange
}) => {
    
    return (
        <Form.Group className="mb-3">
            <Form.Label className="fw-bold">Report</Form.Label>
            {Array.isArray(reports) && reports.length > 0 ?
                <Form.Control
                    as="select"
                    onChange={(e) => {
                        const { value } = e.target;
                        const report = reports.find(r => r.code === value);
                        onChange(report);
                    }}
                    value={currentReport ? currentReport.code : '__NONE__'}
                >
                    <option value="__NONE__">Select a report</option>
                    {reports.map((r, idx) => (
                        <option key={r.code} value={r.code}>{r.label}</option>
                    ))}
                </Form.Control> :
                <div>
                    No reports available
                </div>
            }
        </Form.Group>
    );
}

const CycleSelector = ({
    cycles, currentCycle, onChange
}) => {
    return (
        <Form.Group className="mb-3">
            <Form.Label className="fw-bold">Cycle</Form.Label>
            {Array.isArray(cycles) && cycles.length > 0 ?
                <Form.Control
                    as="select"
                    onChange={(e) => {
                        onChange(e.target.value);
                    }}
                    value={currentCycle || '__NONE__'}
                >
                    <option value="__NONE__">Select a cycle</option>
                    {cycles.map((cycleStr, idx) => (
                        <option key={idx} value={cycleStr}>{cycleStr}</option>
                    ))}
                </Form.Control> :
                <div>
                    No cycle available
                </div>
            }
        </Form.Group>
    );
}

const PeriodSelector = ({
    periodOptions, currentPeriod, onChange
}) => {
    return (
        <Form.Group className="mb-3">
            <Form.Label className="fw-bold">Period</Form.Label>
            {Array.isArray(periodOptions) && periodOptions.length > 0 ?
                <Form.Control
                    as="select"
                    onChange={(e) => {
                        onChange(e.target.value);
                    }}
                    value={currentPeriod || '__NONE__'}
                >
                    <option value="__NONE__">Select a Period</option>
                    {periodOptions.map((periodStr, idx) => {
                        const periodLabel = PERIOD_LIST[periodStr];
                        if (!periodLabel) return null;
                        return <option key={idx} value={periodStr}>{periodLabel}</option>
                    })}
                </Form.Control> :
                <div>
                    No period available
                </div>
            }
        </Form.Group>
    );
}

export const FFDAReports = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [reportOptions, setReportOptions] = useState({});
    const [currentChapel, setCurrentChapel] = useState(null);
    const [currentReport, setCurrentReport] = useState(null);
    const [currentCycle, setCurrentCycle] = useState(null);
    const [currentPeriod, setCurrentPeriod] = useState(null);
    const refreshReportOptions = async () => {
        setIsLoading(true);
        const repOptionRes = await getReportOptions();
        setReportOptions(repOptionRes.data);
        setIsLoading(false);
    };
    const handleChapelChange = (chapel) => {
        setCurrentReport(null);
        setCurrentCycle(null);
        setCurrentPeriod(null);
        setCurrentChapel(chapel);
    };
    const handleReportChange = (report) => {
        const r = (report ? reportOptions.reports[report.code] : null)
        setCurrentCycle(Object.keys(r).includes('defaultCycle') ? r.defaultCycle : null);
        setCurrentPeriod(Object.keys(r).includes('defaultPeriod') ? r.defaultPeriod : null);
        setCurrentReport(r ? {
            ...r,
            code: report.code,
        } : null);
    }
    const reportList = useMemo(() => {
        if (currentChapel) {
            return REPORT_LIST.filter(report => report.canAccess(currentChapel, reportOptions));
        }
        return null;
    }, [currentChapel]);
    const cycleList = useMemo(() => {
        if (!currentReport) {
            return null;
        }
        if (!currentPeriod && currentReport.periodOptions > 0) {
            return null;
        }
        const cycleFromList = currentReport.cycleFrom;
        if (Array.isArray(cycleFromList)) {
            const usedPeriod = ReportPeriodToNumber(currentPeriod);
            if (cycleFromList.length > usedPeriod) {
                return reportOptions.cycleList[cycleFromList[usedPeriod]];
            }
            return null;
        }
        return reportOptions.cycleList[currentReport.cycleFrom]
    }, [currentReport, currentPeriod, reportOptions.cycleList])
    useEffect(() => {
        refreshReportOptions();
    }, []);
    const canDownload = useMemo(() => {
        return currentReport && currentChapel && currentCycle && (
            currentReport.periodOptions > 0 ?
                currentPeriod != null : true
        );
    }, [currentReport, currentChapel, currentCycle, currentPeriod]);
    const handleDownload = async (e) => {
        e.preventDefault();
        e.target.blur();
        setIsLoading(true);
        try {
            const usedPeriod = ReportPeriodToNumber(currentPeriod);
            const pdf = await getHashReport(currentCycle, currentReport.reportType, currentChapel.chapel, usedPeriod);
            const pdfHash = await getReportHashed(pdf.file_hash);
            window.open(pdfHash);
        } catch (e) {
            console.error(e);
        }
        setIsLoading(false);
    };

    return (
        <>
            {isLoading ? <AppOverlay show label="Getting latest report information"/> : null}
            <MainLayout pageTitle={T('REPORTS_EXPORTS_TITLE')}>
                <Container fluid>
                    <Row className="justify-content-center">
                        <Col xs={12} sm={10} md={8} lg={6} xl={4}>
                            <Card>
                                <Card.Body>
                                    <Card.Title>Generate a financial report</Card.Title>
                                    <Card.Body>
                                        <ChapelSelector
                                            reportOptions={reportOptions}
                                            onChange={handleChapelChange}
                                            currentChapel={currentChapel}
                                        />
                                        <ReportSelector
                                            reports={reportList}
                                            currentReport={currentReport}
                                            onChange={handleReportChange}
                                        />
                                        <PeriodSelector
                                            periodOptions={currentReport ? currentReport.periodOptions : null}
                                            currentPeriod={currentPeriod}
                                            onChange={(value) => {
                                                setCurrentPeriod(value);
                                                setCurrentCycle(null);
                                            }}
                                        />
                                        <CycleSelector
                                            cycles={cycleList}
                                            currentCycle={currentCycle}
                                            onChange={setCurrentCycle}
                                        />
                                        <div className="d-flex justify-content-center">
                                            <Button
                                                onClick={handleDownload}
                                                disabled={!canDownload}
                                            >Download report</Button>
                                        </div>
                                    </Card.Body>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </MainLayout>
        </>
    );
}

export default FFDAReports;